import type { Request, Response, RequestHandler } from 'express';
import { LogWatcher } from './log-watcher';
import { ClientManager } from './client-manager';

interface SSEHandlerOptions {
  debug?: boolean;
  heartbeatInterval?: number; // ms, default 30000
}

/**
 * Create SSE handler for server logs streaming
 *
 * Endpoint: GET /dev/logs/server-logs/stream
 *
 * SSE Events:
 * - connected: { clientId: string } - sent when client connects
 * - log: ServerLog - sent when new log entry is written
 * - heartbeat: { timestamp: number } - sent periodically to keep connection alive
 */
export function createSSEHandler(logDir: string, options: SSEHandlerOptions = {}): RequestHandler {
  const { debug = false, heartbeatInterval = 30000 } = options;

  // Shared instances across all connections
  const logWatcher = new LogWatcher({ debug });
  const clientManager = new ClientManager({ debug });

  const log = (...args: unknown[]): void => {
    if (debug) {
      console.log('[SSEHandler]', ...args);
    }
  };

  return (req: Request, res: Response): void => {
    // Set SSE headers
    res.setHeader('Content-Type', 'text/event-stream');
    res.setHeader('Cache-Control', 'no-cache');
    res.setHeader('Connection', 'keep-alive');
    res.setHeader('X-Accel-Buffering', 'no'); // Disable nginx buffering

    // Disable response timeout
    res.setTimeout(0);

    // Generate client ID and register
    const clientId = clientManager.generateClientId();
    clientManager.addClient(clientId, res);

    log(`New SSE connection: ${clientId}`);

    // Start log watcher if this is the first client
    if (clientManager.getClientCount() === 1) {
      log('First client connected, starting log watcher');
      logWatcher.start(logDir);
    }

    // Subscribe to new logs
    const unsubscribe = logWatcher.onLog((logEntry) => {
      clientManager.sendToClient(clientId, 'log', logEntry);
    });

    // Send connected event
    clientManager.sendToClient(clientId, 'connected', {
      clientId,
      timestamp: Date.now(),
    });

    // Set up heartbeat
    const heartbeat = setInterval(() => {
      const success = clientManager.sendToClient(clientId, 'heartbeat', {
        timestamp: Date.now(),
      });

      if (!success) {
        // Client disconnected, clean up
        clearInterval(heartbeat);
      }
    }, heartbeatInterval);

    // Handle client disconnect
    const cleanup = (): void => {
      log(`Client disconnected: ${clientId}`);

      clearInterval(heartbeat);
      unsubscribe();
      clientManager.removeClient(clientId);

      // Stop log watcher if no more clients
      if (clientManager.getClientCount() === 0) {
        log('No more clients, stopping log watcher');
        logWatcher.stop();
      }
    };

    req.on('close', cleanup);
    req.on('error', (error) => {
      log(`Client error ${clientId}:`, error);
      cleanup();
    });
  };
}

/**
 * Create a handler that returns current SSE status
 * Useful for debugging/monitoring
 */
export function createSSEStatusHandler(
  clientManager: ClientManager,
  logWatcher: LogWatcher
): RequestHandler {
  return (_req: Request, res: Response): void => {
    res.json({
      clients: clientManager.getClientCount(),
      clientIds: clientManager.getClientIds(),
      subscribers: logWatcher.getSubscriberCount(),
    });
  };
}
